import React, { useContext, useState, useEffect, useRef } from "react";
import { Table, Input, Button, Popconfirm, Form, InputNumber, Space, message } from "antd";
import { useTraits } from "views/SYSubjects/hooks/Index";
import { LockOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";

import { generateString } from "helpers/randomStringGenerator";

import { useMutation } from "react-query";
import http from "services/httpService.js";

const EditableContext = React.createContext();

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  dataType,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef();
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      //   inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async (e) => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;
  if (editable) {
    childNode = editing ? (
      <Form.Item
        className="ant-form-item-custom"
        style={{
          margin: 0,
        }}
        name={dataIndex}
        initialValue={record[dataIndex]}
      >
        {dataType === "number" ? (
          <InputNumber
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            min={0.0}
            max={100.0}
            step={0.01}
          />
        ) : (
          <Input ref={inputRef} onPressEnter={save} onBlur={save} />
        )}
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const Traits = () => {
  const { isLoading, data: traitsdata } = useTraits();
  const [tablePagination, setTablePagination] = useState({
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Traits`,
    position: ["topRight", "bottomRight"],
    showSizeChanger: true,
    pageSize: 10,
  });

  const [mutate] = useMutation(
    async (values) => {
      await http.put(`traits/update`, {
        values: values,
      });
    },
    {
      onSuccess: (data, variables) => {
        message.success("Record saved.");
      },
      onError: () => {
        // setDrawerVisibility(false);
        message.warning("Unable to save, Something went wrong :/");
      },
    }
  );

  const [mutateDelete] = useMutation(
    async (traits_code) => {
      await http.delete(`traits/delete/${traits_code}`);
    },
    {
      onSuccess: (data, variables) => {
        message.success("Record deleted.");
      },
      onError: () => {
        // setDrawerVisibility(false);
        message.warning("Unable to save, Something went wrong :/");
      },
    }
  );

  const columns = [
    {
      title: () => (
        <>
          <Space>
            <span>
              <b>Traits</b>
            </span>
          </Space>
        </>
      ),
      dataIndex: "traits",
      editable: true,
    },
    {
      title: "",
      dataIndex: "operation",
      width: 65,
      render: (text, record) => {
        if (data.length <= 0) {
          return null;
        } else {
          if (record.locked === 1) {
            return <Button type="link" icon={<LockOutlined style={{ color: "#ffc53d" }} />} />;
          } else {
            return (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => handleDelete(record.traits_code)}
              >
                <Button type="link" icon={<DeleteOutlined />} />
              </Popconfirm>
            );
          }
        }
      },
    },
  ];
  const [data, setData] = useState([]);
  useEffect(() => {
    setData(traitsdata);
  }, [traitsdata]);

  const handleDelete = (traits_code) => {
    mutateDelete(traits_code);
    setData(data.filter((item) => item.traits_code !== traits_code));
  };

  const handleAdd = () => {
    setTablePagination({
      ...tablePagination,
      current: Math.ceil(data.length / tablePagination.pageSize),
    });
    let newCode = generateString(6);
    const newData = {
      traits_code: newCode,
      traits: `{  Type traits name here...  }`,
      locked: 0,
    };
    setData([...data, newData]);
  };

  const handleSave = (row) => {
    const newData = [...data];
    const index = newData.findIndex((item) => row.traits_code === item.traits_code);
    const item = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    setData(newData);
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };
  const columnss = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave: handleSave,
      }),
    };
  });
  const handleTableChange = async (pagination, filters, sorter) => {
    console.log(pagination.pageSize);
    setTablePagination({
      ...tablePagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  return (
    <div>
      <Space>
        <Button onClick={() => handleAdd()} type="primary" icon={<PlusOutlined />}>
          Add Traits
        </Button>
        <Button
          onClick={() => {
            mutate([...data]);
          }}
          type="primary"
          icon={<SaveOutlined />}
        >
          Save
        </Button>
      </Space>

      <Table
        loading={isLoading}
        components={components}
        rowClassName={() => "editable-row"}
        dataSource={data}
        columns={columnss}
        size="small"
        rowKey="traits_code"
        onChange={handleTableChange}
        pagination={tablePagination}
        total={100}
      />
    </div>
  );
};

export default Traits;
